<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

export default {
    name: "LeadSourceMap",
    props: {
        markers: {
            type: Array,
            default: () => [],
        },
        center: {
            type: Array,
            default: () => [15, 25],
        },
        zoom: {
            type: Number,
            default: 1,
        },
        pitch: {
            type: Number,
            default: 28,
        },
        bearing: {
            type: Number,
            default: -18,
        },
    },
    data() {
        return {
            map: null,
            markerInstances: [],
            autoRotateTimer: null,
            isInteracting: false,
        };
    },
    computed: {
        mapboxToken() {
            return import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || "";
        },
        isTokenMissing() {
            return !this.mapboxToken;
        },
        sidebarCountries() {
            return [...this.markers].sort((a, b) => b.value - a.value);
        },
    },
    mounted() {
        this.initializeMap();
        window.addEventListener("resize", this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.handleResize);
        this.clearAutoRotate();
        this.removeMarkers();
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    },
    watch: {
        markers: {
            deep: true,
            handler() {
                this.renderMarkers();
            },
        },
    },
    methods: {
        initializeMap() {
            if (this.isTokenMissing || !this.$refs.mapRoot) return;

            mapboxgl.accessToken = this.mapboxToken;
            this.map = new mapboxgl.Map({
                container: this.$refs.mapRoot,
                style: "mapbox://styles/mapbox/streets-v12",
                center: this.center,
                zoom: this.zoom,
                pitch: this.pitch,
                bearing: this.bearing,
                attributionControl: false,
                projection: "globe",
                dragRotate: true,
            });

            this.map.addControl(new mapboxgl.NavigationControl({showCompass: false}), "top-right");
            this.map.on("load", () => {
                if (typeof this.map.setFog === "function") {
                    this.map.setFog({
                        color: "rgb(220, 234, 255)",
                        "high-color": "rgb(195, 218, 255)",
                        "horizon-blend": 0.16,
                        "space-color": "rgb(187, 211, 248)",
                        "star-intensity": 0,
                    });
                }

                this.renderMarkers();
                this.scheduleAutoRotate();
            });

            this.map.on("mousedown", this.markInteracting);
            this.map.on("touchstart", this.markInteracting);
            this.map.on("dragstart", this.markInteracting);
            this.map.on("pitchstart", this.markInteracting);
            this.map.on("rotatestart", this.markInteracting);
            this.map.on("moveend", this.handleMoveEnd);
        },
        handleResize() {
            if (this.map) {
                this.map.resize();
            }
        },
        removeMarkers() {
            this.markerInstances.forEach((marker) => marker.remove());
            this.markerInstances = [];
        },
        markInteracting() {
            this.isInteracting = true;
            this.clearAutoRotate();
        },
        handleMoveEnd() {
            this.isInteracting = false;
            this.scheduleAutoRotate();
        },
        clearAutoRotate() {
            if (this.autoRotateTimer) {
                clearTimeout(this.autoRotateTimer);
                this.autoRotateTimer = null;
            }
        },
        scheduleAutoRotate() {
            if (!this.map) return;

            this.clearAutoRotate();

            this.autoRotateTimer = setTimeout(() => {
                if (!this.map || this.isInteracting) return;
                if (this.map.getZoom() > 2.2) return;

                const center = this.map.getCenter();
                this.map.easeTo({
                    center: [center.lng - 14, center.lat],
                    duration: 2400,
                    easing: (t) => t,
                });
            }, 900);
        },
        markerScale(size) {
            if (size === "xl") return 42;
            if (size === "lg") return 36;
            if (size === "md") return 31;
            return 27;
        },
        countryFlag(code) {
            const normalized = (code || "").toUpperCase();
            if (!/^[A-Z]{2}$/.test(normalized)) return "🏳";

            return String.fromCodePoint(...[...normalized].map((char) => 127397 + char.charCodeAt(0)));
        },
        renderMarkers() {
            if (!this.map || !this.map.isStyleLoaded()) return;

            this.removeMarkers();

            this.markers.forEach((item) => {
                if (typeof item.lng !== "number" || typeof item.lat !== "number") return;

                const markerElement = document.createElement("div");
                markerElement.className = "lead-map-marker";
                markerElement.style.width = `${this.markerScale(item.size)}px`;
                markerElement.style.height = `${this.markerScale(item.size)}px`;
                markerElement.innerHTML = `<span>${item.value}</span>`;
                markerElement.title = `${item.country}: ${item.value} leads`;

                const marker = new mapboxgl.Marker({element: markerElement, anchor: "center"}).setLngLat([item.lng, item.lat]).addTo(this.map);

                this.markerInstances.push(marker);
            });
        },
    },
};
</script>

<template>
    <div class="lead-source-map">
        <div v-if="isTokenMissing" class="lead-source-map__fallback">
            <strong>Mapbox token missing</strong>
            <span>Add VITE_MAPBOX_ACCESS_TOKEN in .env to enable map view.</span>
        </div>
        <template v-else>
            <aside class="lead-source-map__sidebar">
                <div v-for="item in sidebarCountries" :key="item.country" class="lead-source-map__country-row">
                    <span class="lead-source-map__flag">{{ countryFlag(item.code) }}</span>
                    <span class="lead-source-map__country-name">{{ item.country }}</span>
                    <strong class="lead-source-map__count">{{ item.value }}</strong>
                </div>
            </aside>
            <div ref="mapRoot" class="lead-source-map__canvas" />
        </template>
    </div>
</template>

<style scoped>
.lead-source-map {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 300px;
}

.lead-source-map__sidebar {
    position: absolute;
    left: 12px;
    top: 12px;
    z-index: 2;
    width: 190px;
    display: grid;
    gap: 6px;
    padding: 10px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid rgba(204, 215, 232, 0.9);
    backdrop-filter: blur(4px);
}

.lead-source-map__country-row {
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #1f2937;
}

.lead-source-map__flag {
    font-size: 14px;
    line-height: 1;
}

.lead-source-map__country-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.lead-source-map__count {
    color: #1d4ed8;
    font-size: 12px;
}

.lead-source-map__canvas {
    width: 100%;
    height: 100%;
    min-height: inherit;
}

.lead-source-map__fallback {
    width: 100%;
    min-height: inherit;
    border-radius: 12px;
    border: 1px dashed var(--site-border);
    background: linear-gradient(180deg, #fafbfc 0%, #f3f5f8 100%);
    color: var(--site-text-secondary);
    display: grid;
    place-items: center;
    gap: 8px;
    text-align: center;
    padding: 20px;
}

:deep(.mapboxgl-ctrl-top-right) {
    top: 8px;
    right: 8px;
}

:deep(.mapboxgl-ctrl-group) {
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.1);
    border: 1px solid #dbe3ef;
    border-radius: 10px;
}

:deep(.lead-map-marker) {
    border-radius: 999px;
    background: rgba(29, 78, 216, 0.8);
    border: 1px solid rgba(30, 64, 175, 0.72);
    color: #fff;
    display: grid;
    place-items: center;
    text-align: center;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 8px 20px rgba(29, 78, 216, 0.24);
}

:deep(.lead-map-marker span) {
    display: block;
    font-size: 11px;
}

@media (max-width: 900px) {
    .lead-source-map__sidebar {
        position: static;
        width: 100%;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        margin-bottom: 8px;
    }
}

@media (max-width: 640px) {
    .lead-source-map__sidebar {
        grid-template-columns: 1fr;
    }
}
</style>
